import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { GlobalConstants } from '@globalConstants';
import { ICourse } from '@interfaces/ICourse';
import { IGroup } from '@interfaces/IGroup';
import { IUser } from '@interfaces/IUser';
import { IAppState } from '@myStore/app.state';
import {
  ClearActiveCourses,
  GetActiveCoursesRequest,
} from '@myStore/courses-view/courses-view.actions';
import { selectCoursesList } from '@myStore/courses-view/courses-view.selector';
import {
  ClearTransfersStudentsToGroupErrors,
  SendTransfersStudentsToGroupRequest,
} from '@myStore/groups-changes/groups-changes.actions';
import {
  selectChangesGroupLoading,
  selectChangesGroupSuccess,
} from '@myStore/groups-changes/groups-changes.selector';
import {
  ClearStudentsTransfersGroups,
  GetStudentsTransfersGroupsRequest,
} from '@myStore/groups-view/groups-view.actions';
import { selectStudentsTransfersGroups } from '@myStore/groups-view/groups-view.selector';
import {
  ClearUsers,
  GetStudentsRequest,
  GetUsersRequest,
} from '@myStore/users-view/users-view.actions';
import {
  selectStudents,
  selectUsers,
  selectUsersLoading,
} from '@myStore/users-view/users-view.selector';
import { select, Store } from '@ngrx/store';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-users-form-in-group',
  templateUrl: './transferring-students-to-group.html',
  styleUrls: ['./transferring-students-to-group.sass'],
})
export class TransferringStudentsToGroupComponent implements OnInit, OnDestroy {
  @ViewChild('selectedUsers', { static: true }) selectedUsers!: ElementRef;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public loading$ = this._store.pipe(select(selectUsersLoading));
  public sendLoading$ = this._store.pipe(select(selectChangesGroupLoading));
  public role!: number;
  public userRoles = GlobalConstants.UserRolesToServer;
  public groupId!: number;
  public checkedUsers = new Set<number>();
  public students: IUser[] | null = null;
  public initialStudents: IUser[] | null = null;
  public isAllChecked = false;
  public searchValue: string | null = null;
  public courses: ICourse[] | null = null;
  public groups: IGroup[] | null = null;

  public filterForm = new UntypedFormGroup({
    course: new UntypedFormControl(),
    group: new UntypedFormControl({ value: null, disabled: true }),
  });

  constructor(
    private _store: Store<IAppState>,
    private _dialogRef: MatDialogRef<FormDialogComponent>,
    private _route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const dialogData = this._dialogRef.componentInstance.data;
    if (dialogData.role && dialogData.groupId) {
      this.role = dialogData.role;
      this.groupId = dialogData.groupId;
    }

    this._store.dispatch(new GetActiveCoursesRequest());

    this._store
      .pipe(select(selectStudents), takeUntil(this.destroy$))
      .subscribe(students => {
        this.students = students ? students.results : null;
        this.initialStudents = students ? students.results : null;
      });

    this._store
      .pipe(select(selectUsers), takeUntil(this.destroy$))
      .subscribe(users => {
        if (users) {
          this.students = users ? users.results : null;
        }
      });

    this._store
      .pipe(select(selectCoursesList), takeUntil(this.destroy$))
      .subscribe(list => {
        this.courses = list ? list : [];
      });

    this._store
      .pipe(select(selectChangesGroupSuccess), takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          this.onCancel();
          const queryParams = this._route.snapshot.queryParams.q;

          if (!queryParams || queryParams === 'active') {
            this._store.dispatch(
              new GetStudentsRequest(this.groupId, { status: 'active' })
            );
          }
        }
      });

    this.filterForm.controls['course'].valueChanges.subscribe(() => {
      const courseControl = this.filterForm.controls.course;
      const groupControl = this.filterForm.controls.group;

      this._store.dispatch(
        new GetStudentsTransfersGroupsRequest(courseControl.value)
      );

      this._store
        .pipe(select(selectStudentsTransfersGroups))
        .subscribe(groups => {
          this.groups = groups
            ? groups.results.filter(group => group.id !== +this.groupId)
            : null;
          groupControl.patchValue(null);
        });

      if (groupControl.disabled && !this.groups) {
        groupControl.enable();
      }
    });
  }

  onSubmit(): void {
    if (this.checkedUsers.size === 0) return;

    this._store.dispatch(
      new SendTransfersStudentsToGroupRequest(this.groupId, {
        students: [...this.checkedUsers],
        group: this.filterForm.controls.group.value,
      })
    );
  }

  onCancel() {
    this._dialogRef.close();
  }

  isDisabledSubmitButton(): boolean {
    return this.checkedUsers.size === 0;
  }

  isDisabledSearchField(): boolean {
    return !(
      this.filterForm.controls.group.value &&
      this.filterForm.controls.course.value
    );
  }

  toggleCheckUser(id: number): void {
    this.checkedUsers.has(id)
      ? this.checkedUsers.delete(id)
      : this.checkedUsers.add(id);
  }

  isUserChecked(id: number): boolean {
    return this.checkedUsers.has(id);
  }

  setAll() {
    if (this.isAllChecked) {
      this.isAllChecked = false;
      this.checkedUsers.clear();
    } else {
      this.isAllChecked = true;
      this.students?.forEach((student: IUser) => {
        if (student.id) {
          this.checkedUsers.add(student.id);
        }
      });
    }
  }

  searchUsers(value: string): void {
    this.searchValue = value;

    if (value) {
      this._store.dispatch(
        new GetUsersRequest({
          qparams: {
            edu_group_id: this.groupId,
            search: value,
          },
        })
      );
    } else {
      this.students = this.initialStudents;
    }
  }

  ngOnDestroy() {
    this._store.dispatch(new ClearUsers());
    this._store.dispatch(new ClearStudentsTransfersGroups());
    this._store.dispatch(new ClearTransfersStudentsToGroupErrors());
    this._store.dispatch(new ClearActiveCourses());
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
